<template>
  <CCanvasModal show :title="title" @close="emit('cancel')">
    <NForm ref="formRef" class="p-4" requireMarkPlacement="right" :show-feedback="false" size="medium">
      <NGrid :cols="1" :y-gap="16">
        <NFormItemGridItem :label="labels.name" required v-bind="bindValidation('name')">
          <NInput v-model:value="form.name" :placeholder="placeholders.name" />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.status" v-bind="bindValidation('status')" required>
          <CSelect v-model:value="form.status" bordered :options="statusOptions" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="launchDarkly.enable_create_section_from_section_template && templateOptions.length"
          :label="labels.template_id"
        >
          <CSelect
            v-model:value="form.template_id"
            bordered
            clearable
            :disabled="isLoading"
            filterable
            label-field="name"
            :loading="isFetchingTemplates"
            :options="templateOptions"
            required
            :show-checkmark="false"
            value-field="id"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.tags">
          <CTags
            v-model:value="form.tags"
            :suggested-tags-fetcher="
              props.canvasType !== 'section-template'
                ? (search) =>
                    fetchSuggestedInstanceTags(search, {
                      type: ['canvases'],
                      canvasType: props.canvasType as InstanceTagsCanvasType,
                    })
                : undefined
            "
          />
        </NFormItemGridItem>
      </NGrid>
    </NForm>

    <template #footer>
      <CButton @click="emit('cancel')">
        {{ t('canvasUI.common.cancel') }}
      </CButton>

      <CButton
        :disabled="!!v.$silentErrors.length"
        :loading="isLoading"
        type="primary"
        @click="emit('submit', getFormValues(form))"
      >
        <template #icon>
          <CIcon icon="plus" />
        </template>

        {{ createButtonText }}
      </CButton>
    </template>
  </CCanvasModal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { ref, computed, reactive, inject, type ComputedRef, onMounted } from 'vue'
import { NForm, NFormItemGridItem, NGrid, NInput } from 'naive-ui'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCanvasModal from '@lib/components/CCanvasModal/CCanvasModal.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import { CanvasStatus, type CanvasType } from '@lib/types/canvases'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import useBindValidation from '@lib/composables/useBindValidation'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import type { CanvasRetrieve, CanvasCreateRequest } from '@openapi'
import type { InstanceTagsCanvasType } from '@lib/types/instanceTags.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

type SubmitPayload = Omit<CanvasCreateRequest, 'instance_id'>

interface Props {
  isLoading?: boolean
  canvasType: Exclude<CanvasType, 'canvas'>
  sectionTemplatesFetcher?: (params?: Record<string, any>) => Promise<CanvasRetrieve[]>
}

interface Form {
  name: CanvasRetrieve['name']
  status: CanvasStatus
  tags: CanvasRetrieve['tags']
  template_id: CanvasRetrieve['id']
}

const props = withDefaults(defineProps<Props>(), {
  isSection: false,
  isTemplate: false,
  isLoading: false,
})

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'submit', form: SubmitPayload): void
}>()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const { t } = useI18n()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const isFetchingTemplates = ref(false)
const templateOptions = ref<CanvasRetrieve[]>([])
const form: Form = reactive({
  name: '',
  status: CanvasStatus.DRAFT,
  tags: [],
  template_id: '',
})
const labels: Record<keyof Form, string> = {
  name: t('canvasUI.CCreateTemplateSectionBlockModal.form.label.name'),
  status: t('canvasUI.CCreateTemplateSectionBlockModal.form.label.status'),
  tags: t('canvasUI.CCreateTemplateSectionBlockModal.form.label.tags'),
  template_id: t('canvasUI.CCreateTemplateSectionBlockModal.form.label.template'),
}
const placeholders: Partial<Record<keyof Form, string>> = {
  name: t('canvasUI.CCreateTemplateSectionBlockModal.form.placeholder.add', { field: labels.name }),
}
const statusOptions = [
  {
    label: t('canvasUI.CCreateTemplateSectionBlockModal.form.options.status.draft'),
    value: CanvasStatus.DRAFT,
  },
  {
    label: t('canvasUI.CCreateTemplateSectionBlockModal.form.options.status.ready'),
    value: CanvasStatus.READY,
  },
]
const rules = {
  name: { required },
  status: { required },
}
const v = useVuelidate(rules, form, { $autoDirty: true })
const requiredMsg = (field: keyof typeof labels) =>
  t('canvasUI.CCreateTemplateSectionBlockModal.form.validation.required', { field: labels[field] })
const { bindValidation } = useBindValidation<Form>(v, {
  name: { required: { msg: requiredMsg('name') } },
  status: { required: { msg: requiredMsg('status') } },
})

function getFormValues(form: Form): SubmitPayload {
  return {
    ...form,
    is_draft: CanvasStatus.DRAFT === form.status,
    is_template: props.canvasType === 'template' || props.canvasType === 'section-template',
    is_section: props.canvasType === 'section-template' || props.canvasType === 'section',
    is_block: props.canvasType === 'block',
  }
}

const title = computed(() => {
  if (props.canvasType === 'section-template') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.titleForSectionTemplate')
  } else if (props.canvasType === 'section') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.titleForSection')
  } else if (props.canvasType === 'template') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.titleForTemplate')
  } else if (props.canvasType === 'block') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.titleForBlock')
  } else {
    return ''
  }
})

const createButtonText = computed(() => {
  if (props.canvasType === 'section-template') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.createSectionTemplate')
  } else if (props.canvasType === 'section') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.createSection')
  } else if (props.canvasType === 'template') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.createTemplate')
  } else if (props.canvasType === 'block') {
    return t('canvasUI.CCreateTemplateSectionBlockModal.createBlock')
  } else {
    return ''
  }
})

onMounted(() => {
  if (props.sectionTemplatesFetcher && launchDarkly.value.enable_create_section_from_section_template) {
    isFetchingTemplates.value = true
    props
      .sectionTemplatesFetcher()
      .then((templates) => {
        templateOptions.value = templates
      })
      .finally(() => {
        isFetchingTemplates.value = false
      })
  }
})
</script>
